import React from 'react';
import Icon from '../../../components/AppIcon';
import { getHolidaysForDate } from '../../../utils/holidayService';

const getActivityStyle = (activity, persons) => {
  const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
  const personColors = activity?.personColors || (activity?.personColor ? [activity?.personColor] : []);
  
  if (personIds?.length === 0 || personColors?.length === 0) {
    return {
      backgroundColor: '#e5e7eb15',
      borderLeft: '4px solid #e5e7eb'
    };
  }
  
  const allIds = persons?.map(p => p?.id) || [];
  const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
  
  if (allSelected) {
    return {
      background: 'linear-gradient(90deg, #ef4444, #f59e0b, #10b981, #3b82f6, #6366f1, #a855f7)',
      borderLeft: '4px solid #a855f7',
      backgroundSize: '200% 100%',
      animation: 'rainbow-shift 3s ease infinite'
    };
  } else if (personColors?.length === 1) {
    return {
      backgroundColor: `${personColors?.[0]}15`,
      borderLeft: `4px solid ${personColors?.[0]}`
    };
  } else {
    const gradientStops = personColors?.map((color, idx) => {
      const position = (idx / (personColors?.length - 1)) * 100;
      return `${color} ${position}%`;
    })?.join(', ');
    
    return {
      background: `linear-gradient(90deg, ${gradientStops})`,
      borderLeft: `4px solid ${personColors?.[0]}`,
      opacity: 0.9
    };
  }
};

const getActivityTextColor = (activity, persons) => {
  const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
  const allIds = persons?.map(p => p?.id) || [];
  const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
  
  if (allSelected || (activity?.personColors?.length > 1)) {
    return '#ffffff';
  }
  
  return '#1f2937';
};

const DayView = ({ 
  currentDate, 
  activities, 
  onActivityClick,
  onDragStart,
  onDrop,
  preferences,
  persons = []
}) => {
  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  const getActivitiesForDate = () => {
    const dateStr = currentDate?.toISOString()?.split('T')?.[0];
    return activities?.filter(activity => activity?.date === dateStr);
  };

  const getHolidaysForDay = () => {
    return getHolidaysForDate(
      currentDate,
      preferences?.holidayLocation,
      preferences?.showPublicHolidays,
      preferences?.showSchoolTerms
    );
  };

  const dayActivities = getActivitiesForDate();
  const dayHolidays = getHolidaysForDay();

  const getActivitiesForHour = (hour) => {
    return dayActivities?.filter(activity => {
      const activityDate = new Date(activity.date);
      const activityHour = parseInt(activity?.time?.split(':')?.[0]);
      
      return activityDate?.getDate() === currentDate?.getDate() &&
             activityDate?.getMonth() === currentDate?.getMonth() &&
             activityDate?.getFullYear() === currentDate?.getFullYear() &&
             activityHour === hour;
    });
  };

  const formatTime = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const handleDrop = (e, hour) => {
    e?.preventDefault();
    e?.currentTarget?.classList?.remove('bg-muted');
    const newDate = new Date(currentDate);
    newDate?.setHours(hour, 0, 0, 0);
    onDrop(e, newDate);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.currentTarget?.classList?.add('bg-muted');
  };

  const handleDragLeave = (e) => {
    e?.currentTarget?.classList?.remove('bg-muted');
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm-md overflow-hidden">
      <div className="border-b border-border bg-muted/50 p-4 md:p-6">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">
          {currentDate?.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </h3>
      </div>
      {/* Holiday Indicators */}
      {dayHolidays?.length > 0 && (
        <div className="mb-4 space-y-2">
          {dayHolidays?.map((holiday, idx) => (
            <div
              key={idx}
              className={`text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${
                holiday?.type === 'public' ?'bg-red-100 text-red-700 border border-red-200' 
                  : holiday?.type === 'school' ?'bg-blue-100 text-blue-700 border border-blue-200' :'bg-yellow-100 text-yellow-700 border border-yellow-200'
              }`}
            >
              <Icon 
                name={holiday?.type === 'public' ? 'calendar' : 'graduation-cap'} 
                size={16} 
              />
              <span className="font-medium">{holiday?.name}</span>
            </div>
          ))}
        </div>
      )}
      {/* Activities */}
      <div className="divide-y divide-border max-h-[600px] md:max-h-[700px] overflow-y-auto">
        {timeSlots?.map((hour) => {
          const hourActivities = getActivitiesForHour(hour);
          
          return (
            <div 
              key={hour}
              className="grid grid-cols-[100px_1fr] md:grid-cols-[120px_1fr] min-h-[80px] md:min-h-[100px] hover:bg-muted/50 transition-colors"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, hour)}
            >
              <div className="p-3 md:p-4 border-r border-border">
                <div className="text-sm md:text-base font-medium text-muted-foreground">
                  {formatTime(hour)}
                </div>
              </div>
              <div className="p-3 md:p-4">
                <div className="space-y-2">
                  {hourActivities?.map((activity) => {
                    const activityStyle = getActivityStyle(activity, persons);
                    const textColor = getActivityTextColor(activity, persons);
                    const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
                    const personNames = activity?.personNames || (activity?.personName ? [activity?.personName] : []);
                    const personInitials = activity?.personInitials || (activity?.personInitials ? [activity?.personInitials] : []);
                    const personColors = activity?.personColors || (activity?.personColor ? [activity?.personColor] : []);
                    const allIds = persons?.map(p => p?.id) || [];
                    const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
                    
                    return (
                      <div
                        key={activity?.id}
                        className="p-3 md:p-4 rounded-lg cursor-move transition-all hover:scale-[1.02] hover:shadow-warm-md"
                        style={activityStyle}
                        draggable
                        onDragStart={(e) => onDragStart(e, activity)}
                        onClick={() => onActivityClick(activity)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm md:text-base mb-1 truncate" style={{ color: textColor }}>
                              {activity?.title}
                              {allSelected && <span className="ml-1">✨</span>}
                            </h4>
                            <div className="flex items-center gap-2 text-xs md:text-sm" style={{ color: textColor, opacity: 0.9 }}>
                              <Icon name="clock" size={14} />
                              <span>{activity?.time}</span>
                              <span>•</span>
                              <span className="font-medium">
                                {allSelected ? 'All Family' : personNames?.join(', ')}
                              </span>
                            </div>
                            {activity?.location && (
                              <div className="flex items-center gap-2 text-xs mt-1" style={{ color: textColor, opacity: 0.8 }}>
                                <Icon name="map-pin" size={12} />
                                <span>{activity?.location}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            {allSelected ? (
                              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                <span className="text-white text-lg">✨</span>
                              </div>
                            ) : personInitials?.length === 1 ? (
                              <div
                                className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: personColors?.[0] }}
                              >
                                <span className="text-white text-xs md:text-sm font-bold">
                                  {personInitials?.[0]}
                                </span>
                              </div>
                            ) : (
                              <div className="flex -space-x-2">
                                {personInitials?.slice(0, 3)?.map((initials, idx) => (
                                  <div
                                    key={idx}
                                    className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center border-2 border-white"
                                    style={{ backgroundColor: personColors?.[idx] }}
                                  >
                                    <span className="text-white text-[10px] md:text-xs font-bold">
                                      {initials}
                                    </span>
                                  </div>
                                ))}
                                {personInitials?.length > 3 && (
                                  <div className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center border-2 border-white bg-gray-500">
                                    <span className="text-white text-[10px] font-bold">
                                      +{personInitials?.length - 3}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {hourActivities?.length === 0 && (
                    <div className="text-xs md:text-sm text-muted-foreground italic py-2">
                      No activities scheduled
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DayView;